import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import StatCard from "../components/StatCard";
import CustomPieChart from "../components/CustomPieChart";
import CustomBarChart from "../components/CustomBarChart";
import MapDash from "../components/MapDash";
import SplashScreen from "../components/SplashScreen";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const Waste = () => {
  const [data, setData] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [showBranchSelect, setShowBranchSelect] = useState(false);
  const [cityBranches, setCityBranches] = useState([]);
  const [selectedBranchIndex, setSelectedBranchIndex] = useState(0);
  const [mapData, setMapData] = useState([]);
  const { t } = useTranslation();

  const defaultWasteData = [
    {
      city: "",
      generation_collection: {
        total_generated_tpd: {
          MSW: 0,
          "C&D": 0,
          other: 0,
        },
        collection_efficiency_percent: 0,
        open_container_overflow_rate_percent: 0,
        recyclable_generated_tpd: {
          بلاستيك: 0,
          ورق: 0,
        },
        collected_uco_liters: {
          مطعم_أ: 0,
          مطعم_ب: 0,
        },
        collected_uco_liters_month: {
          يناير: 0,
          فبراير: 0,
        },
      },
      processing_diversion: {
        overall_diversion_rate_percent: 0,
        biodiesel_from_uco_liters: 0,
        overall_recycling_rate_percent: 0,
        source_segregation: {
          participation_rate_percent: 0,
        },
        organic_waste_composting_percent: 0,
      },
      hazardous_waste: {
        medical_waste_tpm: 0,
        weee_collected_tpm: 0,
        weee_processing: {
          recycled_refurbished_percent: 0,
          hazardous_material_removed_percent: 0,
        },
      },
      circular_economy_community_engagement: {
        number_of_active_local_initiatives: 0,
        stakeholder_engagement: {
          events_count: 0,
          estimated_participants: 0,
        },
      },
    },
  ];

  const navigate = useNavigate();

  useEffect(() => {
    const localData = localStorage.getItem("wasteFormData");

    if (localData) {
      try {
        const parsed = JSON.parse(localData);
        if (Array.isArray(parsed)) {
          setData(parsed);
          setFilteredData(parsed);
        } else {
          setData(defaultWasteData);
          setFilteredData(defaultWasteData);
        }
      } catch (error) {
        setData(defaultWasteData);
        setFilteredData(defaultWasteData);
      }
    } else {
      setData(defaultWasteData);
      setFilteredData(defaultWasteData);
    }
  }, []);

  const transformToNumbers = (obj) => {
    for (const key in obj) {
      if (
        typeof obj[key] === "object" &&
        obj[key] !== null &&
        !Array.isArray(obj[key])
      ) {
        transformToNumbers(obj[key]);
      } else if (!isNaN(obj[key]) && obj[key] !== "") {
        obj[key] = Number(obj[key]);
      }
    }
  };

  const mergeDataForCity = (entries) => {
    if (entries.length === 1) return entries;
    setMapData(entries);
    entries.forEach((entry) => transformToNumbers(entry));

    const merged = JSON.parse(JSON.stringify(entries[0]));

    for (let i = 1; i < entries.length; i++) {
      const current = entries[i];

      const deepMerge = (target, source) => {
        for (const key in source) {
          if (
            typeof source[key] === "object" &&
            source[key] !== null &&
            !Array.isArray(source[key])
          ) {
            if (!target[key]) target[key] = {};
            deepMerge(target[key], source[key]);
          } else if (typeof source[key] === "number") {
            target[key] = (target[key] || 0) + source[key];
          }
        }
      };

      deepMerge(merged, current);
    }

    return [merged];
  };

  const handleCityChange = (event) => {
    const city = event.target.value;
    setSelectedCity(city);

    if (city) {
      const filtered = data.filter((item) => item.city === city);

      if (filtered.length > 1) {
        setShowBranchSelect(true);
        setCityBranches(filtered);
      } else {
        setShowBranchSelect(false);
      }

      const merged = mergeDataForCity(filtered);
      setFilteredData(merged);
    } else {
      setFilteredData(data);
      setShowBranchSelect(false);
    }
  };

  if (!Array.isArray(filteredData)) {
    return <SplashScreen />;
  }

  const cities = Array.from(new Set(data.map((item) => item.city)));
  const filteredCities = cities.filter((city) => city);

  useEffect(() => {
    if (filteredCities.length > 0 && !selectedCity) {
      setSelectedCity(filteredCities[0]);
      const initialFiltered = data.filter(
        (item) => item.city === filteredCities[0]
      );
      setFilteredData(mergeDataForCity(initialFiltered));
    }
  }, [filteredCities, selectedCity, data]);

  const recyclableData = Object.entries(
    filteredData[0]?.generation_collection?.recyclable_generated_tpd || {}
  ).map(([name, value]) => ({ name, value }));

  const ucoData = Object.entries(
    filteredData[0]?.generation_collection?.collected_uco_liters || {}
  ).map(([name, value]) => ({ name, value }));

  const ucoMonthlyData = Object.entries(
    filteredData[0]?.generation_collection?.collected_uco_liters_month || {}
  ).map(([name, value]) => ({ name, value }));

  const weeeProcessingData = [
    {
      name: t("recycled_refurbished"),
      value:
        filteredData[0]?.hazardous_waste?.weee_processing
          ?.recycled_refurbished_percent ?? 0,
    },
    {
      name: t("hazardous_material_removed"),
      value:
        filteredData[0]?.hazardous_waste?.weee_processing
          ?.hazardous_material_removed_percent ?? 0,
    },
  ];

  const circularEngagementData = [
    {
      name: t("active_local_initiatives"),
      value:
        filteredData[0]?.circular_economy_community_engagement
          ?.number_of_active_local_initiatives ?? 0,
    },
    {
      name: t("engagement_events"),
      value:
        filteredData[0]?.circular_economy_community_engagement
          ?.stakeholder_engagement?.events_count ?? 0,
    },
    {
      name: t("participants"),
      value:
        filteredData[0]?.circular_economy_community_engagement
          ?.stakeholder_engagement?.estimated_participants ?? 0,
    },
  ];

  return (
    <>
      <Helmet>
        <title>{t("waste")} | {t("dashboard")}</title>
      </Helmet>

      <div className="flex flex-col gap-4 md:gap-3 text-start w-full h-auto md:h-full md:min-h-0 md:overflow-hidden">
        <div className="flex flex-col gap-2 text-start">
          <h1 className="mx-auto text-xl sm:text-2xl md:text-3xl font-extrabold p-2.5 bg-white/55 rounded-md backdrop-blur-md w-full flex justify-center">
            {t("waste")}
          </h1>
          <div className="flex flex-col sm:flex-row gap-2 w-full justify-between items-center">
            <select
              name="city"
              className="border p-2 rounded w-full sm:w-1/2 md:w-1/3 lg:w-1/4 dark:bg-gray-600 dark:text-white bg-white text-xs sm:text-sm"
              value={selectedCity}
              onChange={handleCityChange}
            >
              <option disabled value="">
                {t("average_all_cities")}
              </option>
              {filteredCities.map((city, idx) => (
                <option key={idx} value={city}>
                  {city}
                </option>
              ))}
            </select>

            {showBranchSelect && (
              <select
                className="border p-2 rounded w-full sm:w-1/3 dark:bg-gray-600 dark:text-white bg-white text-xs sm:text-sm"
                onChange={(e) =>
                  setSelectedBranchIndex(parseInt(e.target.value))
                }
                value={selectedBranchIndex ?? ""}
              >
                <option disabled value="">
                  {t("choose_branch")}
                </option>
                {cityBranches.map((item, idx) => (
                  <option key={idx} value={idx}>
                    {item.city} - {idx + 1}
                  </option>
                ))}
              </select>
            )}

            <button
              onClick={() => navigate("/WasteForm")}
              className="bg-green-500 hover:bg-green-600 transition-colors text-white p-2 rounded w-full sm:w-auto text-xs sm:text-sm font-semibold mt-2 sm:mt-0"
            >
              {t("edit_data")}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 flex-1 min-h-0">
          <div
            className="col-span-1 overflow-y-auto pr-2 h-[60vh] md:h-auto md:flex-1 md:min-h-0 flex flex-col gap-4"
            dir="ltr"
          >
            {(showBranchSelect && selectedBranchIndex !== null
              ? [filteredData[selectedBranchIndex]]
              : filteredData
            ).map((item, index) => (
              <div key={index}>
                <section>
                  <h2 className="text-lg font-bold mb-2 text-start">
                    {t("waste_generation_collection")}
                  </h2>
                  <div className="grid grid-cols-3 mb-4 gap-2">
                    <StatCard
                      title={t("solid_waste")}
                      value={
                        item.generation_collection?.total_generated_tpd?.MSW ??
                        0
                      }
                    />
                    <StatCard
                      title={t("cd_waste")}
                      value={
                        item.generation_collection?.total_generated_tpd?.[
                          "C&D"
                        ] ?? 0
                      }
                    />
                    <StatCard
                      title={t("other_waste")}
                      value={
                        item.generation_collection?.total_generated_tpd
                          ?.other ?? 0
                      }
                    />
                  </div>
                  <div className="grid grid-cols-2 mb-4 gap-2">
                    <StatCard
                      title={t("collection_efficiency")}
                      value={`${
                        item.generation_collection
                          ?.collection_efficiency_percent ?? 0
                      }%`}
                    />
                    <StatCard
                      title={t("open_container_overflow")}
                      value={`${
                        item.generation_collection
                          ?.open_container_overflow_rate_percent ?? 0
                      }%`}
                    />
                  </div>
                  <div className="grid grid-cols-1 mb-4 gap-2">
                    <CustomBarChart
                      data={ucoMonthlyData}
                      xKey="name"
                      barKey="value"
                      title={t("collected_uco_time")}
                    />
                    <CustomBarChart
                      data={ucoData}
                      xKey="name"
                      barKey="value"
                      title={t("biodiesel_uco")}
                    />
                  </div>
                  <div className="grid grid-cols-1">
                    <CustomPieChart
                      data={recyclableData}
                      dataKey="value"
                      nameKey="name"
                      title={t("recyclable_waste")}
                    />
                  </div>
                </section>

                <section className="mt-5">
                  <h2 className="text-lg font-bold mb-2 text-start">
                    {t("waste_processing_diversion")}
                  </h2>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <StatCard
                      title={t("diversion_rate")}
                      value={`${
                        item.processing_diversion
                          ?.overall_diversion_rate_percent ?? 0
                      }%`}
                    />
                    <StatCard
                      title={t("biodiesel_uco")}
                      value={`${
                        item.processing_diversion?.biodiesel_from_uco_liters ??
                        0
                      } L`}
                    />
                    <StatCard
                      title={t("overall_recycling_rate")}
                      value={`${
                        item.processing_diversion
                          ?.overall_recycling_rate_percent ?? 0
                      }%`}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <StatCard
                      title={t("source_segregation")}
                      value={`${
                        item.processing_diversion?.source_segregation
                          ?.participation_rate_percent ?? 0
                      }%`}
                    />
                    <StatCard
                      title={t("organic_waste_composting")}
                      value={`${
                        item.processing_diversion
                          ?.organic_waste_composting_percent ?? 0
                      }%`}
                    />
                  </div>
                </section>

                <section className="mt-5">
                  <h2 className="text-lg font-bold mb-2 text-start">
                    {t("hazardous_waste_management")}
                  </h2>

                  <div className="grid grid-cols-1 gap-2">
                    <div className="grid grid-cols-2 gap-2 h-auto">
                      <StatCard
                        title={t("medical_waste")}
                        value={item.hazardous_waste?.medical_waste_tpm ?? 0}
                        className="flex items-center justify-center flex-col"
                      />
                      <StatCard
                        title={t("weee_waste")}
                        value={item.hazardous_waste?.weee_collected_tpm ?? 0}
                        className="flex items-center justify-center flex-col"
                      />
                    </div>
                  </div>

                  <div className="mt-2">
                    <CustomPieChart
                      data={weeeProcessingData}
                      dataKey="value"
                      nameKey="name"
                      title={t("hazardous_waste_management")}
                    />
                  </div>
                </section>

                <section className="mt-5">
                  <h2 className="text-lg font-bold mb-2 text-start">
                    {t("circular_economy_engagement")}
                  </h2>
                  <div className="grid grid-cols-1 gap-2">
                    <CustomBarChart
                      data={circularEngagementData}
                      xKey="name"
                      barKey="value"
                      title={t("circular_economy_engagement")}
                    />{" "}
                    <div className="grid grid-cols-2 gap-2 h-auto">
                      <StatCard
                        title={t("active_local_initiatives")}
                        value={
                          item.circular_economy_community_engagement
                            ?.number_of_active_local_initiatives ?? 0
                        }
                      />
                      <StatCard
                        title={t("engagement_events")}
                        value={
                          item.circular_economy_community_engagement
                            ?.stakeholder_engagement?.events_count ?? 0
                        }
                      />
                    </div>
                  </div>
                </section>
              </div>
            ))}
          </div>

          {/* العمود الثاني - الخريطة مرة واحدة فقط */}
          <div className="md:col-span-2 h-[60vh] md:h-auto md:flex-1 md:min-h-0 rounded-xl leaflet-container !bg-transparent min-h-[300px] shadow-md border border-white/20">
            <MapDash initialData={mapData} />
          </div>
        </div>
      </div>
    </>
  );
};

export default Waste;


